// onboarding-login-guard.ts
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { AuthBootstrapService } from '../Services/Authorization/auth-bootstrap.service';
import { OnboardingContextService } from '../Shared/utilities';
import { PatientOnboarding } from '../Services/API/patient-onboarding-api';

/**
 * Stateless URL-token validation for the QR onboarding entry route.
 * Always hits the validation API on entry — no early-return based on cached
 * onboarding/hospital signals. If the URL token is valid, OnboardingContext
 * is populated for the component to consume; if not, /noAccess.
 */
export const onboardingLoginGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authBootstrap = inject(AuthBootstrapService);
  const patientOnboarding = inject(PatientOnboarding);
  const onboardingContextService = inject(OnboardingContextService);

  const token = route.paramMap.get('token');
  if (!token) {
    router.navigate(['/noAccess']);
    return false;
  }

  // Wait for the service-level JWT, then validate the URL onboarding token.
  return from(authBootstrap.ensureToken()).pipe(
    switchMap(() =>
      patientOnboarding.getValidatePatientPortalLoginGUIDToken(token)
    ),
    map((resp: any) => {
      if (resp.Status === 'Success') {
        onboardingContextService.setContext({
          AppointmentId:  resp.rows[0].AppointmentId,
          DeleteInd:      resp.rows[0].DeleteInd,
          ExpiresAt:      resp.rows[0].ExpiresAt,
          OrganizationId: resp.rows[0].OrganizationId,
          EmailId:        resp.rows[0].EmailId,
          PatientId:      resp.rows[0].PatientId,
          PortalTokenId:  resp.rows[0].PortalTokenId,
          Token:          resp.rows[0].Token,
          MobileNumber:   resp.rows[0].MobileNo
        });
        return true;
      }
      router.navigate(['/noAccess']);
      return false;
    }),
    catchError(err => {
      console.error('Token validation failed', err);
      router.navigate(['/noAccess']);
      return of(false);
    })
  );
};
